import { inject, Injectable, NgZone } from "@angular/core";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { FirebaseMessaging, Notification } from "@capacitor-firebase/messaging";
import { Subject } from "rxjs";
import { MESSAGING_CONFIG } from "@app/config/messaging.config";
import { MessagingProvider, PushAction, PushMessage, PushPermission } from "./messaging.types";

@Injectable({ providedIn: "root" })
export class FcmMessagingProvider implements MessagingProvider {
  readonly name = "fcm";
  private readonly zone = inject(NgZone);
  private readonly config = inject(MESSAGING_CONFIG);
  private readonly tokens = new Subject<string>();
  private readonly received = new Subject<PushMessage>();
  private readonly opened = new Subject<PushAction>();
  readonly token$ = this.tokens.asObservable();
  readonly received$ = this.received.asObservable();
  readonly opened$ = this.opened.asObservable();
  private handles: PluginListenerHandle[] = [];
  private initialization?: Promise<void>;

  initialize(): Promise<void> {
    return this.initialization ??= this.attach().catch(async error => {
      await this.dispose();
      throw error;
    });
  }

  private async attach(): Promise<void> {
    this.handles.push(await FirebaseMessaging.addListener("tokenReceived", event => {
      this.zone.run(() => this.tokens.next(event.token));
    }));
    this.handles.push(await FirebaseMessaging.addListener("notificationReceived", event => {
      this.zone.run(() => this.received.next(this.normalize(event.notification)));
    }));
    this.handles.push(await FirebaseMessaging.addListener("notificationActionPerformed", event => {
      this.zone.run(() => this.opened.next({ actionId: event.actionId, message: this.normalize(event.notification) }));
    }));
    if (Capacitor.getPlatform() === "android") {
      await FirebaseMessaging.createChannel({ ...this.config.androidChannel, importance: 4, vibration: true });
    }
  }

  async checkPermission(): Promise<PushPermission> { return (await FirebaseMessaging.checkPermissions()).receive; }
  async requestPermission(): Promise<PushPermission> { return (await FirebaseMessaging.requestPermissions()).receive; }
  async getToken(): Promise<string> { return (await FirebaseMessaging.getToken()).token; }

  async dispose(): Promise<void> {
    const handles = this.handles.splice(0);
    await Promise.all(handles.map(handle => handle.remove()));
    this.initialization = undefined;
  }

  private normalize(message: Notification): PushMessage {
    return {
      id: message.id, title: message.title, body: message.body,
      data: message.data && typeof message.data === "object" && !Array.isArray(message.data)
        ? message.data as Record<string, unknown> : {},
    };
  }
}
